import type {
  BulkUpdateAppNotificationsBody,
  GetAppNotificationsResponse,
  GetUnreadNotificationCountsResponse,
  UpdateAppNotificationBody,
} from "@nlc/shared-types";
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { appNotificationsKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

export type AppNotificationsTag = "mention" | "request" | undefined;

export function useAppNotifications({
  isArchived = false,
  itemsPerPage = 10,
  tag,
}: {
  isArchived?: boolean;
  itemsPerPage?: number;
  tag?: AppNotificationsTag;
}): UseInfiniteQueryResult<InfiniteData<GetAppNotificationsResponse>> {
  return useInfiniteQuery<GetAppNotificationsResponse>({
    getNextPageParam: (lastPage) =>
      lastPage.metadata.currentPage < lastPage.metadata.totalPages
        ? lastPage.metadata.currentPage + 1
        : undefined,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      apiClient
        .url("/notifications")
        .query({ isArchived, itemsPerPage, page: pageParam, tag })
        .get()
        .json<GetAppNotificationsResponse>(),
    queryKey: appNotificationsKeys.list({ isArchived, itemsPerPage, tag }),
  });
}

export function useUnreadNotificationCounts(): UseQueryResult<GetUnreadNotificationCountsResponse> {
  return useQuery({
    queryFn: () =>
      apiClient
        .url("/notifications/unread-counts")
        .get()
        .json<GetUnreadNotificationCountsResponse>(),
    queryKey: appNotificationsKeys.unreadCounts(),
  });
}

export function useUpdateAppNotification(): UseMutationResult<
  Response,
  Error,
  { body: UpdateAppNotificationBody; id: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      body,
      id,
    }: {
      body: UpdateAppNotificationBody;
      id: number;
    }) => apiClient.url(`/notifications/${id}`).patch(body).res(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appNotificationsKeys.all });
    },
  });
}

export function useBulkUpdateAppNotifications(): UseMutationResult<
  Response,
  Error,
  BulkUpdateAppNotificationsBody
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: BulkUpdateAppNotificationsBody) =>
      apiClient.url("/notifications/bulk").patch(body).res(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appNotificationsKeys.all });
    },
  });
}
